import React, { ReactElement, useEffect } from "react";
import { GameState } from "../types";
import { buildTips } from "./tips";
import "./TipsPanel.css";

const STORAGE_KEY = "shoTipsOpen";

/** Whether the tips were left open the last time (they are closed for a new player). */
export function readTipsOpen(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch (e) {
    return false;
  }
}

export function writeTipsOpen(open: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, open ? "1" : "0");
  } catch (e) {
    // Remembering it is a nicety.
  }
}

type TipsPanelProps = {
  gameState: GameState;
  user: string;
  onClose: () => void;
};

/**
 * A sidebar on the right with short tips for the moment: what the current step asks of you, what the next
 * policies do on this board, and a little advice for your role. It follows the board, so it never gets ahead of
 * the animations, and it only uses what the player is allowed to know.
 */
export default function TipsPanel(props: TipsPanelProps): ReactElement {
  const sections = buildTips(props.gameState, props.user);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [props]);

  return (
    <aside className="tp-panel" aria-label="Tips">
      <div className="tp-head">
        <h2 className="tp-title">Tips</h2>
        <button className="tp-close" aria-label="Close the tips" onClick={props.onClose}>
          ×
        </button>
      </div>
      <div className="tp-body">
        {sections.map((section) => (
          <section key={section.title} className="tp-section">
            <h3>{section.title}</h3>
            <ul>
              {section.tips.map((tip, i) => (
                <li key={i} className={tip.tone ? "tp-" + tip.tone : undefined}>
                  {tip.text}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </aside>
  );
}
